import { createSlice } from '@reduxjs/toolkit';

/**
 * @typedef {object} BoostCredits
 * @property {number} granted
 * @property {number} consumed
 * @property {number} remaining
 * @property {string|null} resetsAt
 */

/**
 * @typedef {object} BoostPrice
 * @property {string} priceId
 * @property {string} duration
 * @property {number} amountCents
 * @property {string} currency
 */

/**
 * @typedef {object} ActiveBoost
 * @property {string} id
 * @property {string} duration
 * @property {string} expiresAt
 */

/**
 * @typedef {object} BoostState
 * @property {BoostCredits|null} credits
 * @property {BoostPrice[]} prices
 * @property {Record<string, ActiveBoost|null>} activeBoosts  Keyed by Sharetribe listing UUID.
 */

/** @type {BoostState} */
const initialState = {
  credits: null,
  prices: [],
  activeBoosts: {},
};

const slice = createSlice({
  name: 'boost',
  initialState,
  reducers: {
    /**
     * Replace credits / prices and merge per-listing active boosts. Used by
     * the `boostApi.fetchBoosts` success path to hydrate every card on a
     * page in one shot.
     */
    hydrate: (state, action) => {
      const { credits, prices, activeBoosts } = action.payload || {};
      if (credits) state.credits = credits;
      if (prices) state.prices = prices;
      if (activeBoosts) {
        Object.entries(activeBoosts).forEach(([listingId, ab]) => {
          state.activeBoosts[listingId] = ab;
        });
      }
    },

    /**
     * Set the active boost for a single listing. Used by `applyBoost` to
     * reflect a freshly applied boost without a re-fetch.
     */
    setActiveBoost: (state, action) => {
      const { listingId, activeBoost } = action.payload || {};
      if (!listingId) return;
      state.activeBoosts[listingId] = activeBoost || null;
    },

    /**
     * Decrement remaining credits by 1, never below 0. Used after a
     * successful pro-credit apply so the dialog reflects new balance
     * without a re-fetch.
     */
    decrementCredits: state => {
      if (state.credits) {
        state.credits = {
          ...state.credits,
          consumed: (state.credits.consumed || 0) + 1,
          remaining: Math.max(0, (state.credits.remaining || 0) - 1),
        };
      }
    },
  },
});

export const { hydrate, setActiveBoost, decrementCredits } = slice.actions;

/**
 * Selector: full boost state slice.
 * @param {{ boost: BoostState }} state
 * @returns {BoostState}
 */
export const selectBoostState = state => state.boost;

/**
 * Selector: credits row (or null when not yet hydrated).
 * @param {{ boost: BoostState }} state
 */
export const selectBoostCredits = state => state.boost.credits;

/**
 * Selector: cached Stripe boost prices.
 * @param {{ boost: BoostState }} state
 */
export const selectBoostPrices = state => state.boost.prices;

/**
 * Selector factory: active boost for a single listing.
 * @param {string} listingId
 */
export const selectActiveBoostForListing = listingId => state =>
  state.boost.activeBoosts[listingId] ?? null;

export default slice.reducer;
